// @givmo/org-console — the query keys, and the set an access change moves. In one place.
//
// A write on this surface is never local to the panel that made it. Changing one admin's access moves
// the roster, the CALLER's own standing (`/me` carries what they may do right now), and the access
// event log — and switching a module off org-wide moves all of those plus the settings map. So the
// invalidation set is a fact about the domain, not about a component, and it lives here rather than
// being re-derived per call site.
//
// ⚠ THIS EXISTS BECAUSE A BARE `invalidateQueries()` IS THE WRONG TOOL HERE, twice over. It re-reads
// every query in the host app — including ones an access change cannot touch — and, when awaited
// before a dialog closes, it makes that close wait on all of them. The measured symptom on the
// surface this is ported from was an editor that saved successfully and then sat open behind a
// spinner until unrelated reads settled.
//
// ⚠ EVERY KEY IS SCOPED BY `orgRef`. A host may switch between organizations without unmounting, and
// an unscoped key would serve one organization's roster to another for as long as it stayed fresh.
//
// The keys are hand-written for the same reason the URLs are — there are no generated query-option
// factories for `/org`. They are shaped like generated ones (a stable prefix plus the scope) so a
// future regen can replace them without re-keying every cache entry.

const ROOT = 'org-console';

/** Every read this package makes, keyed by organization. */
export function orgConsoleKeys(orgRef: string) {
  return {
    members: [ROOT, 'members', orgRef] as const,
    // ⚠ A CHILD of `members`, deliberately: the exhausted-roster read (the who-can-open counts) must
    // be invalidated by exactly the writes that invalidate the roster, and a prefix match on
    // `members` reaches it only if the org ref sits before the discriminator.
    membersAll: [ROOT, 'members', orgRef, 'all'] as const,
    catalog: [ROOT, 'catalog', orgRef] as const,
    settings: [ROOT, 'settings', orgRef] as const,
    events: [ROOT, 'events', orgRef] as const,
    self: [ROOT, 'self', orgRef] as const,
    connectedAi: [ROOT, 'connected-ai', orgRef] as const,
    measurement: [ROOT, 'measurement', orgRef] as const,
    invites: [ROOT, 'invites', orgRef] as const,
    /** Every access-requests list for this organization — the prefix a write invalidates. */
    accessRequests: [ROOT, 'access-requests', orgRef] as const,
    preferences: [ROOT, 'preferences', orgRef] as const,
    /** Every module's terms read for this organization — the prefix; one module's is `orgModuleTermsKey`. */
    terms: [ROOT, 'terms', orgRef] as const,
    /** The organization's setting definitions + values (OM-17 seam A) — distinct from the switches (`settings`). */
    orgSettings: [ROOT, 'org-settings', orgRef] as const,
  };
}

/**
 * ONE access-requests list — the org-wide view (`module === null`) or a module's own view.
 *
 * The module filter is IN the key, so the org-wide list and a module's list are separate entries
 * that both sit under the `accessRequests` prefix a write invalidates. `'*'` is the org-wide
 * discriminator rather than an absent element, so the two can never collide with each other.
 */
export function orgAccessRequestsKey(orgRef: string, module: string | null) {
  return [...orgConsoleKeys(orgRef).accessRequests, module ?? '*'] as const;
}

/**
 * ONE invite list — the org-wide view (`module === null`) or a module's own view, the same shape
 * as the request table's key: the module is IN the key because the server scopes the module view
 * (`?module=`), and both entries sit under the `invites` prefix an invite write invalidates.
 */
export function orgInvitesKey(orgRef: string, module: string | null) {
  return [...orgConsoleKeys(orgRef).invites, module ?? '*'] as const;
}

/** One module's terms read. */
export function orgModuleTermsKey(orgRef: string, module: string) {
  return [...orgConsoleKeys(orgRef).terms, module] as const;
}

/**
 * Every key an INVITE write — offering one, or rescinding one — invalidates.
 *
 * ⚠ THE LIST, AND ONLY THE LIST. An offer is not a membership: nobody's access moves until the
 * invitee redeems it, so the roster, the caller's own standing, the switches and the access event
 * log are all untouched by both acts. Re-reading them would spend four requests to be told nothing
 * changed — and would quietly teach a reader that offering someone administration already did
 * something to who can act for the organization.
 *
 * ⚠ THE HOST'S `onAccessWritten` SEAM IS NOT FIRED EITHER, for the same reason: it exists for a host
 * rendering something derived from this organization's AUTHORITY, and an outstanding offer is not
 * that. What DOES move authority is the redeem — which happens in the invitee's own session, on a
 * different device, and no cache here can see it.
 */
export function inviteWriteInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  return [orgConsoleKeys(orgRef).invites];
}

/**
 * The redeem's key — the one entry here NOT scoped by an organization, because the caller has no
 * standing in one yet. It is keyed by the TOKEN, which is what makes the redeem survive a remount:
 * a second observer of the same token reads the answer the first one got rather than submitting a
 * single-use token again. See `useOrgInviteRedeem`.
 */
export function orgInviteRedeemKey(token: string): readonly unknown[] {
  return [ROOT, 'invite-redeem', token] as const;
}

/** The preview's key (EP-MNT17): keyed by the TOKEN like the redeem's, and never one organization's. */
export function orgInvitePreviewKey(token: string): readonly unknown[] {
  return [ROOT, 'invite-preview', token] as const;
}

/**
 * Every key an ACCESS write invalidates, in the order a reader would want them back.
 *
 * The roster first (it is what the person is looking at), then their own standing, then the record of
 * what changed, then the org-wide switches.
 */
export function accessWriteInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.members, keys.self, keys.events, keys.settings];
}

/**
 * Every key a REMOVAL invalidates — the roster, the caller's own standing, and the record.
 *
 * ⚠ **THE ORG-WIDE SWITCHES ARE DELIBERATELY ABSENT, which is the one way this differs from
 * {@link accessWriteInvalidations}.** Those switches are a property of the ORGANIZATION — which apps
 * it has turned on — and taking a person off the team moves none of them. Re-reading them would
 * spend a request to be told nothing changed.
 *
 * The caller's own standing IS here, and it is not defensive padding: a Global Admin may remove
 * THEMSELVES when another one remains, and the whole console gates on that read.
 *
 * ⚠ The INVITE list is absent too. A removal does not touch pending invites — an invite is addressed
 * to an email, not to a membership — so a still-outstanding offer to the person just removed is the
 * honest state of the world and the list should keep showing it.
 */
export function removalInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.members, keys.self, keys.events];
}

/**
 * The two existing standing/switch reads a module activation projection moves.
 *
 * A hook result updates the org-wide setting and the caller's standing. It does not mint a status
 * observer or invalidate unrelated roster/event data merely because the Modules page is mounted.
 */
export function moduleActivationInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.self, keys.settings];
}

/**
 * Every key ENDING A CONNECTOR invalidates.
 *
 * The connector list plus the access event log — a revoke appends a row there, and it is the only
 * access change on this surface whose actor is a different human from its subject.
 */
export function connectorWriteInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.connectedAi, keys.events];
}

/**
 * Every key a MEASUREMENT write invalidates: the access event log, and ONLY it.
 *
 * The backend appends one row there per id actually changed, so the record sitting beside the
 * measurement card goes stale the moment a save lands.
 *
 * ⚠ THE CONFIGURATION'S OWN KEY IS DELIBERATELY NOT HERE, and this is the one place in this file
 * where that differs from the other two write sets. The PUT ANSWERS the whole new state, so the
 * mutation writes that answer straight into the cache — invalidating the same key on top of it would
 * spend a second request to be told what the first request already said, and would briefly race the
 * server's answer against a re-read of it.
 *
 * ⚠ AND NOTHING ELSE MOVES. A measurement change touches no grant, no switch and no standing, so the
 * roster, `/me` and the settings map are absent — and the host's `onAccessWritten` seam is not fired
 * either: it exists for a host rendering something derived from this organization's AUTHORITY, and
 * an analytics id is not that.
 */
export function measurementWriteInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  return [orgConsoleKeys(orgRef).events];
}

// ── Shell contract v3 — access requests, preferences, terms ───────────────────

/**
 * Every key CREATING an access request invalidates: the caller's own standing and every requests
 * list.
 *
 * The standing, because the requester's own view rides it — the tile flips to "Access requested"
 * through the standing read's `access_request` sub-object, never through a second observer. The
 * lists, because an approver with the page open should see the new row. Nothing else moves: a
 * request widens nothing.
 */
export function accessRequestCreateInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.self, keys.accessRequests];
}

/**
 * Every key an APPROVAL invalidates — the access-write set, plus every requests list.
 *
 * An approval IS a grant: it writes through the same grant writer an access edit does, so the
 * roster, the caller's own standing (the pending count and, for a self-approval-adjacent read, the
 * grants), the record and the switches all move exactly as for an access write.
 */
export function accessRequestApprovalInvalidations(
  orgRef: string,
): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [...accessWriteInvalidations(orgRef), keys.accessRequests];
}

/**
 * Every key a DENIAL invalidates: the lists, the caller's own standing (its pending count), and
 * the record (the denial appends an event). NOT the roster and NOT the switches — a denial moves
 * nobody's access.
 */
export function accessRequestDenialInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.accessRequests, keys.self, keys.events];
}

/**
 * Every key a PREFERENCE write invalidates: the caller's own standing, and ONLY it.
 *
 * The landing key rides the standing read (`default_landing_module`), so a change to it must
 * re-read that — and nothing else, because a personal preference is not an access change: no
 * roster, no record, no switch moves. The preferences read itself is NOT here for the same reason
 * the measurement key is absent from its own write set: the PUT answers the whole new state and the
 * mutation writes that answer straight into the cache.
 */
export function preferenceWriteInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  return [orgConsoleKeys(orgRef).self];
}

/**
 * Every key an ORG SETTING write invalidates (OM-17 seam A): the access event log and the caller's
 * own `/me` read.
 *
 * An org setting is an org act — each changed key appends one `SETTING_CHANGED` row — so the record
 * beside it goes stale the moment a save lands. The `/me` read serves the organization's time zone in
 * force (`org.time_zone`, chosen in Settings), which every Fundraisers page sets deadlines and emails
 * in, so it is re-read too; no grant, switch or standing moves. The settings read itself is NOT here
 * for the measurement key's reason: the PUT answers the whole new state and the mutation writes that
 * answer straight into the cache.
 */
export function orgSettingWriteInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.events, keys.self];
}

/**
 * Every key ACCEPTING a module's terms invalidates: the org-wide switches (the settings read now
 * says `accepted`) and the caller's own standing. The terms read is written from the accept's own
 * answer rather than re-read.
 */
export function termsAcceptInvalidations(orgRef: string): readonly (readonly unknown[])[] {
  const keys = orgConsoleKeys(orgRef);
  return [keys.settings, keys.self];
}
